import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FilterRule, FilterOperator, ColumnDataType, FilterOption } from '../../types/grid';
import { Filter, ArrowUp, ArrowDown, Plus, Trash2, X, Check } from 'lucide-react';
import { PortalPopover } from '../ui/PortalPopover';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';
import { Input } from '../ui/Input';

export interface ColumnHeaderMenuProps {
  columnKey: string;
  columnName: string;
  dataType?: ColumnDataType;
  options?: FilterOption[];
  isSortable?: boolean;
  sortDirection?: 'asc' | 'desc' | false;
  onSort?: (direction: 'asc' | 'desc' | false) => void;
  isFilterable?: boolean;
  rules: FilterRule[];
  onRulesChange: (newRules: FilterRule[]) => void;
  isOpen?: boolean;
  onToggleOpen?: (open: boolean) => void;
}

export const ColumnHeaderMenu: React.FC<ColumnHeaderMenuProps> = ({
  columnKey,
  columnName,
  dataType = 'text',
  options: propOptions,
  isSortable = true,
  sortDirection = false,
  onSort,
  isFilterable = true,
  rules,
  onRulesChange,
  isOpen,
  onToggleOpen,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isMenuOpen = isOpen !== undefined ? isOpen : uncontrolledOpen;
  const setIsMenuOpen = useCallback(
    (nextOpen: boolean) => {
      if (onToggleOpen) onToggleOpen(nextOpen);
      else setUncontrolledOpen(nextOpen);
    },
    [onToggleOpen]
  );

  const triggerRef = useRef<HTMLButtonElement>(null);

  // Normalize column options (e.g. status)
  const resolvedOptions: FilterOption[] | undefined =
    propOptions && propOptions.length > 0
      ? propOptions
      : dataType === 'status' || columnKey === 'status'
      ? [
          { value: 'OK', label: 'OK', color: 'emerald' },
          { value: 'SPAM', label: 'SPAM', color: 'rose' },
        ]
      : undefined;

  const getOperators = (): Array<{ value: FilterOperator; label: string }> => {
    if (resolvedOptions && resolvedOptions.length > 0) {
      return [
        { value: 'in', label: 'is any of' },
        { value: 'equals', label: 'is' },
        { value: 'not_equals', label: 'is not' },
        { value: 'is_empty', label: 'is empty' },
        { value: 'is_not_empty', label: 'is not empty' },
      ];
    }
    if (dataType === 'date') {
      return [
        { value: 'is_on', label: 'on date' },
        { value: 'is_before', label: 'before' },
        { value: 'is_after', label: 'after' },
        { value: 'is_empty', label: 'is empty' },
        { value: 'is_not_empty', label: 'is not empty' },
      ];
    }
    if (dataType === 'number') {
      return [
        { value: 'equals', label: 'equals' },
        { value: 'not_equals', label: 'does not equal' },
        { value: 'greater_than', label: 'greater than' },
        { value: 'less_than', label: 'less than' },
        { value: 'is_empty', label: 'is empty' },
        { value: 'is_not_empty', label: 'is not empty' },
      ];
    }
    if (dataType === 'boolean') {
      return [{ value: 'boolean', label: 'is' }];
    }
    return [
      { value: 'contains', label: 'contains' },
      { value: 'not_contains', label: 'does not contain' },
      { value: 'equals', label: 'equals' },
      { value: 'not_equals', label: 'does not equal' },
      { value: 'starts_with', label: 'starts with' },
      { value: 'is_empty', label: 'is empty' },
      { value: 'is_not_empty', label: 'is not empty' },
    ];
  };

  const getDefaultOperatorAndValue = (): { operator: FilterOperator; value: string } => {
    if (resolvedOptions && resolvedOptions.length > 0) {
      return { operator: 'in', value: resolvedOptions[0].value };
    }
    if (dataType === 'date') {
      const today = new Date().toISOString().split('T')[0];
      return { operator: 'is_on', value: today };
    }
    if (dataType === 'boolean') {
      return { operator: 'boolean', value: 'true' };
    }
    return { operator: 'contains', value: '' };
  };

  const createDefaultRule = (): FilterRule => {
    const def = getDefaultOperatorAndValue();
    return {
      id: Math.random().toString(36).substring(2, 9),
      columnKey,
      operator: def.operator,
      value: def.value,
    };
  };

  // Local conditions managed inside the popup
  const [localRules, setLocalRules] = useState<FilterRule[]>(() => {
    return rules.length > 0 ? rules.map((r) => ({ ...r })) : [createDefaultRule()];
  });

  const localRulesRef = useRef(localRules);
  localRulesRef.current = localRules;

  // Sync from props only when popover is initially opened
  const prevOpenRef = useRef(isMenuOpen);
  useEffect(() => {
    if (isMenuOpen && !prevOpenRef.current) {
      if (rules.length > 0) {
        setLocalRules(rules.map((r) => ({ ...r })));
      } else {
        setLocalRules([createDefaultRule()]);
      }
    }
    prevOpenRef.current = isMenuOpen;
  }, [isMenuOpen, rules]);

  // Cleanly commit only valid rules to parent
  const commitRules = useCallback(
    (rulesToCommit: FilterRule[]) => {
      const sanitized = rulesToCommit.filter((r) => {
        if (r.operator === 'is_empty' || r.operator === 'is_not_empty') return true;
        if (r.operator === 'boolean') return true;
        return r.value !== undefined && r.value !== null && String(r.value).trim() !== '';
      });
      onRulesChange(sanitized);
    },
    [onRulesChange]
  );

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const scheduleDebouncedCommit = (nextRules: FilterRule[]) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      commitRules(nextRules);
    }, 350);
  };

  const flushCommit = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    commitRules(localRulesRef.current);
  };

  const handleUpdateRule = (ruleId: string, updates: Partial<FilterRule>, isTextTyping = false) => {
    setLocalRules((prev) => {
      const next = prev.map((r) => (r.id === ruleId ? { ...r, ...updates } : r));
      if (isTextTyping) {
        scheduleDebouncedCommit(next);
      } else {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        commitRules(next);
      }
      return next;
    });
  };

  const handleRemoveRule = (ruleId: string) => {
    setLocalRules((prev) => {
      const next = prev.filter((r) => r.id !== ruleId);
      const resulting = next.length > 0 ? next : [createDefaultRule()];
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      commitRules(next);
      return resulting;
    });
  };

  const handleAddCondition = () => {
    const newRule = createDefaultRule();
    setLocalRules((prev) => {
      const next = [...prev, newRule];
      return next;
    });
  };

  const handleClearAll = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    setLocalRules([createDefaultRule()]);
    onRulesChange([]);
  };

  const handleClose = () => {
    flushCommit();
    setIsMenuOpen(false);
  };

  // Toggle multi-select option pill
  const handleToggleOptionValue = (ruleId: string, optVal: string, currentValStr: string) => {
    const currentValues = currentValStr
      ? currentValStr.split(',').map((v) => v.trim()).filter(Boolean)
      : [];
    let nextValues: string[];
    if (currentValues.includes(optVal)) {
      nextValues = currentValues.filter((v) => v !== optVal);
    } else {
      nextValues = [...currentValues, optVal];
    }
    handleUpdateRule(ruleId, { value: nextValues.join(',') }, false);
  };

  const hasActiveFilter = rules.length > 0;

  return (
    <div className="relative inline-flex items-center" onClick={(e) => e.stopPropagation()}>
      {/* Column Filter Icon Button */}
      {isFilterable && (
        <Button
          ref={triggerRef as any}
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={(e) => {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }}
          title={
            hasActiveFilter
              ? `Filter active on ${columnName} (${rules.length})`
              : `Filter ${columnName}`
          }
          className={`relative !p-1 transition-all flex items-center justify-center ${
            hasActiveFilter
              ? '!text-cf-orange !bg-cf-orange/15 hover:!bg-cf-orange/25 border !border-cf-orange/40 shadow-xs'
              : isMenuOpen
              ? '!text-cf-orange !bg-cf-surfaceHover'
              : '!text-cf-textMuted hover:!text-cf-textPrimary hover:!bg-cf-surfaceHover opacity-70 md:opacity-0 md:group-hover/header:opacity-70 md:hover:!opacity-100'
          }`}
        >
          <Filter size={11} className={hasActiveFilter ? 'stroke-[2.5]' : 'stroke-2'} />
          {hasActiveFilter && rules.length > 1 && (
            <span className="absolute -top-1 -right-1 min-w-[12px] h-[12px] px-0.5 rounded-full bg-cf-orange text-cf-bg text-[9px] font-bold flex items-center justify-center leading-none">
              {rules.length}
            </span>
          )}
          {hasActiveFilter && rules.length === 1 && (
            <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-cf-orange ring-1 ring-cf-bg" />
          )}
        </Button>
      )}

      {/* Popover */}
      <PortalPopover
        isOpen={isMenuOpen}
        onClose={handleClose}
        triggerRef={triggerRef}
        align="left"
        className="w-[280px] sm:w-[310px] bg-cf-surface border border-cf-border rounded-lg shadow-2xl p-3 font-sans text-xs select-text"
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-2 border-b border-cf-border mb-2.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <Filter size={12} className="text-cf-orange shrink-0" />
            <span className="font-semibold text-cf-textPrimary truncate" title={columnName}>
              {columnName}
            </span>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={handleClose}
            className="!p-1 text-cf-textMuted hover:text-cf-textPrimary"
            title="Close"
          >
            <X size={13} />
          </Button>
        </div>

        {/* Quick Sort Section */}
        {isSortable && onSort && (
          <div className="mb-3">
            <div className="text-[10px] font-semibold text-cf-textMuted uppercase tracking-wider mb-1.5">
              Sorting
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              <Button
                type="button"
                variant={sortDirection === 'asc' ? 'orange-subtle' : 'secondary'}
                size="sm"
                onClick={() => {
                  onSort(sortDirection === 'asc' ? false : 'asc');
                }}
                leftIcon={
                  <ArrowUp
                    size={12}
                    className={sortDirection === 'asc' ? 'text-cf-orange' : 'text-cf-textMuted'}
                  />
                }
                className="w-full"
              >
                {dataType === 'number'
                  ? 'Sort 1 → 9'
                  : dataType === 'date'
                  ? 'Oldest first'
                  : 'Sort A → Z'}
              </Button>

              <Button
                type="button"
                variant={sortDirection === 'desc' ? 'orange-subtle' : 'secondary'}
                size="sm"
                onClick={() => {
                  onSort(sortDirection === 'desc' ? false : 'desc');
                }}
                leftIcon={
                  <ArrowDown
                    size={12}
                    className={sortDirection === 'desc' ? 'text-cf-orange' : 'text-cf-textMuted'}
                  />
                }
                className="w-full"
              >
                {dataType === 'number'
                  ? 'Sort 9 → 1'
                  : dataType === 'date'
                  ? 'Newest first'
                  : 'Sort Z → A'}
              </Button>
            </div>
          </div>
        )}

        {/* Filter Conditions Section */}
        {isFilterable && (
          <div>
            <div className="flex items-center justify-between text-[10px] font-semibold text-cf-textMuted uppercase tracking-wider mb-1.5">
              <span>Filter Conditions</span>
              {hasActiveFilter && (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleClearAll}
                  className="!p-0 !h-auto text-cf-textMuted hover:text-rose-400 font-normal"
                >
                  Clear filter
                </Button>
              )}
            </div>

            {dataType === 'boolean' ? (
              <div className="space-y-1.5 py-1">
                <div className="text-[10px] text-cf-textMuted uppercase font-semibold">Value</div>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={
                      localRules.some((r) => r.operator === 'boolean' && r.value === 'true')
                        ? 'orange-subtle'
                        : 'secondary'
                    }
                    size="sm"
                    onClick={() => {
                      const isActive = localRules.some((r) => r.operator === 'boolean' && r.value === 'true');
                      if (isActive) {
                        handleClearAll();
                      } else {
                        const rule: FilterRule = {
                          id: Math.random().toString(36).substring(2, 9),
                          columnKey,
                          operator: 'boolean',
                          value: 'true',
                        };
                        setLocalRules([rule]);
                        commitRules([rule]);
                      }
                    }}
                    className="w-full text-center"
                  >
                    is true
                  </Button>
                  <Button
                    type="button"
                    variant={
                      localRules.some((r) => r.operator === 'boolean' && r.value === 'false')
                        ? 'orange-subtle'
                        : 'secondary'
                    }
                    size="sm"
                    onClick={() => {
                      const isActive = localRules.some((r) => r.operator === 'boolean' && r.value === 'false');
                      if (isActive) {
                        handleClearAll();
                      } else {
                        const rule: FilterRule = {
                          id: Math.random().toString(36).substring(2, 9),
                          columnKey,
                          operator: 'boolean',
                          value: 'false',
                        };
                        setLocalRules([rule]);
                        commitRules([rule]);
                      }
                    }}
                    className="w-full text-center"
                  >
                    is false
                  </Button>
                </div>
              </div>
            ) : resolvedOptions && resolvedOptions.length > 0 ? (
              <div className="space-y-1.5 py-1">
                <div className="text-[10px] text-cf-textMuted uppercase font-semibold">Select values</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {resolvedOptions.map((opt) => {
                    const isSelected = localRules.some((r) => r.value === opt.value);
                    return (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={isSelected ? 'orange-subtle' : 'secondary'}
                        size="xs"
                        onClick={() => {
                          if (isSelected) {
                            const next = localRules.filter((r) => r.value !== opt.value);
                            setLocalRules(next);
                            commitRules(next);
                          } else {
                            const newRule: FilterRule = {
                              id: Math.random().toString(36).substring(2, 9),
                              columnKey,
                              operator: 'equals',
                              value: opt.value,
                            };
                            const next = [...localRules.filter((r) => r.value), newRule];
                            setLocalRules(next);
                            commitRules(next);
                          }
                        }}
                        leftIcon={
                          <span
                            className={`w-2 h-2 rounded-full ${
                              opt.color === 'emerald'
                                ? 'bg-emerald-500'
                                : opt.color === 'rose'
                                ? 'bg-rose-500'
                                : 'bg-amber-500'
                            }`}
                          />
                        }
                        rightIcon={isSelected ? <Check size={11} className="shrink-0 ml-0.5" /> : undefined}
                        className={
                          isSelected && opt.color === 'emerald'
                            ? '!bg-emerald-500/20 !text-emerald-400 !border-emerald-500/50'
                            : isSelected && opt.color === 'rose'
                            ? '!bg-rose-500/20 !text-rose-400 !border-rose-500/50'
                            : undefined
                        }
                      >
                        {opt.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="space-y-2 max-h-56 overflow-y-auto pr-0.5">
                {localRules.map((rule) => {
                  const availableOps = getOperators();
                  const isNoVal = rule.operator === 'is_empty' || rule.operator === 'is_not_empty';
                  const isDateOp =
                    rule.operator === 'is_on' || rule.operator === 'is_before' || rule.operator === 'is_after';
                  const isNumericOp = rule.operator === 'greater_than' || rule.operator === 'less_than';

                  return (
                    <div
                      key={rule.id}
                      className="p-2 rounded bg-cf-bg/60 border border-cf-border space-y-1.5"
                    >
                      <div className="flex items-center gap-1.5">
                        {/* Operator Select */}
                        <div className="flex-1 min-w-0">
                          <Select
                            size="xs"
                            value={rule.operator}
                            onChange={(e) =>
                              handleUpdateRule(
                                rule.id,
                                {
                                  operator: e.target.value as FilterOperator,
                                },
                                false
                              )
                            }
                            options={availableOps}
                            className="!h-[28px]"
                          />
                        </div>

                        {/* Remove Condition Button */}
                        {localRules.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-xs"
                            onClick={() => handleRemoveRule(rule.id)}
                            className="text-cf-textMuted hover:text-rose-400 hover:bg-rose-500/10 shrink-0"
                            title="Remove condition"
                          >
                            <Trash2 size={12} />
                          </Button>
                        )}
                      </div>

                      {/* Value row */}
                      {!isNoVal && (
                        <div>
                          {isDateOp || dataType === 'date' ? (
                            <div className="space-y-1.5">
                              <Input
                                size="xs"
                                type="date"
                                value={rule.value}
                                onChange={(e) =>
                                  handleUpdateRule(rule.id, { value: e.target.value }, false)
                                }
                                className="!h-[28px]"
                              />
                              <div className="flex items-center gap-1">
                                {['Today', 'Yesterday', '7d ago'].map((lbl) => {
                                  const now = new Date();
                                  let v = now.toISOString().slice(0, 10);
                                  if (lbl === 'Yesterday') {
                                    const y = new Date(now);
                                    y.setDate(y.getDate() - 1);
                                    v = y.toISOString().slice(0, 10);
                                  } else if (lbl === '7d ago') {
                                    const d7 = new Date(now);
                                    d7.setDate(d7.getDate() - 7);
                                    v = d7.toISOString().slice(0, 10);
                                  }
                                  return (
                                    <Button
                                      key={lbl}
                                      type="button"
                                      variant="secondary"
                                      size="xs"
                                      onClick={() => handleUpdateRule(rule.id, { value: v }, false)}
                                      className="!text-[10px] !px-1.5 !py-0.5 !h-auto border-cf-border/60"
                                    >
                                      {lbl}
                                    </Button>
                                  );
                                })}
                              </div>
                            </div>
                          ) : isNumericOp || dataType === 'number' ? (
                            <Input
                              size="xs"
                              type="number"
                              step="any"
                              value={rule.value}
                              onChange={(e) =>
                                handleUpdateRule(rule.id, { value: e.target.value }, true)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') flushCommit();
                              }}
                              placeholder="Enter number..."
                              className="!h-[28px]"
                            />
                          ) : (
                            <Input
                              size="xs"
                              type="text"
                              value={rule.value}
                              onChange={(e) =>
                                handleUpdateRule(rule.id, { value: e.target.value }, true)
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') flushCommit();
                              }}
                              placeholder={`Filter by ${columnName.toLowerCase()}...`}
                              className="!h-[28px]"
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom Actions */}
            <div className="flex items-center justify-between pt-2 mt-2 border-t border-cf-border">
              {dataType !== 'boolean' && (!resolvedOptions || resolvedOptions.length === 0) ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  onClick={handleAddCondition}
                  leftIcon={<Plus size={12} />}
                  className="!p-0 !h-auto text-cf-orange hover:text-cf-orangeHover"
                >
                  Add condition
                </Button>
              ) : (
                <div />
              )}

              <Button
                type="button"
                variant="secondary"
                size="xs"
                onClick={handleClose}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      </PortalPopover>
    </div>
  );
};
